/**
 * Tell the hub this tab is looking at a view, so others see you there (and you see them).
 * Beats every PRESENCE_BEAT_MS while the tab is visible; leaves when the view closes, the
 * tab hides or the page unloads. Best effort: presence never shows an error.
 */
import { useEffect } from 'react';
import { CSRF_HEADER, PRESENCE_BEAT_MS } from '@orgus/protocol';
import { ApiError, api } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';

function newTabId(): string {
  const bytes = new Uint8Array(9);
  crypto.getRandomValues(bytes);
  return `tab-${Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 14)}`;
}

/** One id per browser tab, kept across reloads of that tab. */
export const TAB_ID: string = (() => {
  try {
    const have = sessionStorage.getItem('as.tab');
    if (have && /^[A-Za-z0-9_-]{6,64}$/.test(have)) return have;
    const id = newTabId();
    sessionStorage.setItem('as.tab', id);
    return id;
  } catch {
    return newTabId();
  }
})();

/** Stop beating after the hub says no (no teams module, or not signed in). */
let disabled = false;

function leave(view: string, keepalive = false): void {
  if (disabled) return;
  if (keepalive) {
    void fetch('/api/presence', {
      method: 'DELETE',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', [CSRF_HEADER]: '1' },
      body: JSON.stringify({ tab: TAB_ID, view }),
    }).catch(() => undefined);
    return;
  }
  void api('/api/presence', { method: 'DELETE', body: { tab: TAB_ID, view } }).catch(() => undefined);
}

export function usePresence(view: string | null): void {
  const mode = useOrgus((s) => s.mode);
  useEffect(() => {
    if (!view || mode !== 'live' || disabled) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    const beat = () => {
      if (disabled || document.visibilityState !== 'visible') return;
      api('/api/presence', { method: 'POST', body: { view, tab: TAB_ID } }).catch((e: unknown) => {
        if (e instanceof ApiError && (e.status === 404 || e.status === 401 || e.status === 403)) {
          disabled = e.status === 404;
          if (timer) clearInterval(timer);
        }
      });
    };
    const onVisibility = () => (document.visibilityState === 'visible' ? beat() : leave(view));
    const onHide = () => leave(view, true);
    beat();
    timer = setInterval(beat, PRESENCE_BEAT_MS);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onHide);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
      leave(view);
    };
  }, [view, mode]);
}
